import { useGraphqlQuery } from "graphql-mock"
import { useQueryParams } from "hooks"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { query } from "./gql"

import s from "./UserFilters.module.scss"

type UserFilters = {
  mandatory?: boolean
  incomplete?: boolean
  deadline_status?: "due" | "overdue"
}

export const UserFiltersConnected = () => {
  const { t } = useTranslation()
  const { data, error, isPending } = useGraphqlQuery(query, {})

  const { queryParams, setQueryParams } = useQueryParams<UserFilters>()
  const { mandatory, incomplete, deadline_status } = queryParams

  const {
    productMandatoryCompleteCount = 0,
    productMandatoryCompleteMax = 0,
    productCompleteCount = 0,
    productCompleteMax = 0,
  } = data?.userProgressSummary ?? {}

  const incompleteCount = productCompleteMax - productCompleteCount
  const mandatoryCount = productMandatoryCompleteCount
  const mandatoryMax = productMandatoryCompleteMax

  /**
   * Toggle of the playthrough status filter.
   * deadline_status filter will clear itself when both mandatory
   * and incomplete filters are turned off.
   */
  const handleFilterToggle = (filterType: "mandatory" | "incomplete") => {
    const getMandatory = () => {
      if (filterType === "mandatory") return mandatory ? undefined : true
      else return mandatory
    }

    const getIncomplete = () => {
      if (filterType === "incomplete") return incomplete ? undefined : true
      else return incomplete
    }

    const getDeadlineStatus = () => {
      const newMandatory = getMandatory()
      const newIncomplete = getIncomplete()

      return !newMandatory && !newIncomplete ? undefined : deadline_status
    }

    setQueryParams({
      mandatory: getMandatory(),
      incomplete: getIncomplete(),
      deadline_status: getDeadlineStatus(),
    })
  }

  if (error) return null

  return (
    <div className={s.filters}>
      <UserFilter
        onClick={() => handleFilterToggle("mandatory")}
        isActive={mandatory}
        variant="mandatory"
        progress={`${mandatoryCount}/${mandatoryMax}`}
        isLoading={isPending}
        text={t("user.MANDATORY")}
      />
      <UserFilter
        onClick={() => handleFilterToggle("incomplete")}
        isActive={incomplete}
        variant="incomplete"
        progress={`${incompleteCount}`}
        isLoading={isPending}
        text={t("user.INCOMPLETE")}
      />
    </div>
  )
}

type UserFilterProps = {
  onClick: () => void
  text: string
  progress: string
  isLoading: boolean
  isActive?: boolean
  variant: "mandatory" | "incomplete"
}
export const UserFilter = ({
  onClick,
  text,
  progress,
  isLoading,
  isActive = false,
  variant,
}: UserFilterProps) => {
  if (isLoading) return <div className={s.skeleton__filter} />

  return (
    <button
      className={modifiers(s, "filter", variant, { isActive })}
      onClick={onClick}
    >
      <div className={s.filter__progress}>{progress}</div>
      <div className={s.filter__text}>{text}</div>
    </button>
  )
}

UserFilter.Error = ({
  progress,
  text,
  variant,
}: {
  progress: string
  text: string
  variant: string
}) => {
  return (
    <button className={modifiers(s, "filter", variant)}>
      <div className={s.filter__progress}>{progress}</div>
      <div className={s.filter__text}>{text}</div>
    </button>
  )
}
